import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { PLATFORMS } from "../lib/brand";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, RefreshCw, X, Ban, CheckCircle2, Layers, Settings2, Wallet, AlertCircle, Loader2, Crown } from "lucide-react";

function Modal({ open, onClose, children, title, testId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testId}>
      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] text-white rounded-xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-500 hover:text-[#4F46E5]" data-testid="modal-close">
          <X size={18} />
        </button>
        <div className="font-display text-2xl font-black tracking-tighter mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}

export default function AdminSellers() {
  const [sellers, setSellers] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [platformTarget, setPlatformTarget] = useState(null);
  const [platformDraft, setPlatformDraft] = useState([]);
  const DEFAULT_PLATFORMS = PLATFORMS.map((p) => p.id);
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    company: "",
    phone: "",
    subscription_days: 30,
    allowed_platforms: [...DEFAULT_PLATFORMS],
  });
  const [newPass, setNewPass] = useState("");
  const [permTarget, setPermTarget] = useState(null);
  const [permDraft, setPermDraft] = useState([]);
  const [creditTarget, setCreditTarget] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [pendingSubs, setPendingSubs] = useState([]);
  const [approving, setApproving] = useState(null);
  const [approvingSub, setApprovingSub] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [tierTarget, setTierTarget] = useState(null); // seller obj for tier modal
  const [tierDraft, setTierDraft] = useState("business");
  const [savingTier, setSavingTier] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [sellersRes, renewalsRes, subsRes] = await Promise.allSettled([
        api.get("/admin/sellers"),
        api.get("/admin/renewal-requests"),
        api.get("/admin/pending-subscriptions"),
      ]);
      if (sellersRes.status === "fulfilled") setSellers(sellersRes.value.data);
      if (renewalsRes.status === "fulfilled") setRenewals(renewalsRes.value.data.requests || []);
      if (subsRes.status === "fulfilled") setPendingSubs(subsRes.value.data.leads || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh();
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    if (form.allowed_platforms.length === 0) {
      toast.error("Select at least one platform");
      return;
    }
    if ((form.password || "").length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await api.post("/admin/sellers", form);
      toast.success("Seller created — credentials emailed");
      setShowCreate(false);
      setForm({
        email: "",
        name: "",
        password: "",
        company: "",
        phone: "",
        subscription_days: 30,
        allowed_platforms: [...DEFAULT_PLATFORMS],
      });
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  function openPlatformEditor(seller) {
    setPlatformTarget(seller);
    setPlatformDraft(seller.allowed_platforms?.length ? [...seller.allowed_platforms] : [...DEFAULT_PLATFORMS]);
  }

  async function onSavePlatforms(e) {
    e.preventDefault();
    if (platformDraft.length === 0) {
      toast.error("Pick at least one platform");
      return;
    }
    try {
      await api.post(`/admin/sellers/${platformTarget.id}/platforms`, { platforms: platformDraft });
      toast.success(`Platforms updated for ${platformTarget.name}`);
      setPlatformTarget(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  function togglePlatformInDraft(pid, target) {
    if (target === "create") {
      setForm((f) => ({
        ...f,
        allowed_platforms: f.allowed_platforms.includes(pid)
          ? f.allowed_platforms.filter((x) => x !== pid)
          : [...f.allowed_platforms, pid],
      }));
    } else {
      setPlatformDraft((d) => (d.includes(pid) ? d.filter((x) => x !== pid) : [...d, pid]));
    }
  }

  async function onDelete(s) {
    setDeleteTarget(s);
    setDeleteConfirm("");
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteConfirm.trim().toLowerCase() !== "delete") {
      toast.error('Please type "delete" to confirm');
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/sellers/${deleteTarget.id}`);
      toast.success(`${deleteTarget.email} removed permanently`);
      setDeleteTarget(null);
      setDeleteConfirm("");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    } finally {
      setDeleting(false);
    }
  }

  async function onRenew(id) {
    try {
      const { data } = await api.post(`/admin/sellers/${id}/renew`, { days: 30 });
      toast.success(`Renewed until ${new Date(data.subscription_expires_at).toLocaleDateString()}`);
      refresh();
    } catch {
      toast.error("Failed");
    }
  }

  async function onToggle(id, current) {
    const next = current === "disabled" ? "active" : "disabled";
    try {
      await api.post(`/admin/sellers/${id}/toggle-status`, { status: next });
      toast.success(`Seller ${next}`);
      refresh();
    } catch {
      toast.error("Failed");
    }
  }

  async function onResetPassword(e) {
    e.preventDefault();
    if ((newPass || "").length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await api.post(`/admin/sellers/${resetTarget.id}/reset-password`, { new_password: newPass });
      toast.success("Password reset");
      setResetTarget(null);
      setNewPass("");
    } catch {
      toast.error("Failed");
    }
  }

  function openPermEditor(seller) {
    setPermTarget(seller);
    setPermDraft([...(seller.paid_features || [])]);
  }

  async function togglePerm(feature) {
    if (!permTarget) return;
    const enabled = !permDraft.includes(feature);
    setPermDraft((d) =>
      enabled ? [...d, feature] : d.filter((f) => f !== feature)
    );
    try {
      await api.post(`/admin/sellers/${permTarget.id}/feature`, {
        feature,
        enabled,
      });
      toast.success(`${feature.replace(/_/g, " ")} ${enabled ? "enabled" : "disabled"}`);
      refresh();
    } catch (err) {
      // Revert local state on failure.
      setPermDraft((d) => (enabled ? d.filter((f) => f !== feature) : [...d, feature]));
      toast.error(errMsg(err, "Failed"));
    }
  }

  async function grantPackage(seller, pkg) {
    try {
      const { data } = await api.post(`/admin/sellers/${seller.id}/grant-credits`, {
        package: pkg,
        amount: 0,
      });
      toast.success(`+${data.added} listings → ${seller.email}`);
      setCreditTarget(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  async function grantCustom(seller, amount) {
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive number");
    try {
      const { data } = await api.post(`/admin/sellers/${seller.id}/grant-credits`, {
        package: "custom",
        amount: n,
      });
      toast.success(`+${data.added} listings → ${seller.email}`);
      setCreditTarget(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  /**
   * NEW (2026-07): admin sets an EXACT listing quota (100/200/500/1000 or
   * any custom number). Overwrites the balance and resets the used counter
   * so the seller starts on a clean N-listing pack. When the seller uses
   * all N, their account auto-locks — they can't generate more until admin
   * runs this again with a new number.
   */
  async function setExactQuota(seller, amount, resetUsed = true) {
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive number");
    try {
      const { data } = await api.post(`/admin/sellers/${seller.id}/set-listing-quota`, {
        count: n,
        reset_used: resetUsed,
      });
      toast.success(
        `Quota set → ${data.total_quota} listings · ${data.remaining} remaining (${seller.email})`
      );
      setCreditTarget(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  async function approveRenewal(req) {
    setApproving(req.id);
    try {
      await api.post(`/admin/renewal-requests/${req.id}/approve`);
      const credits = req.package === "starter_100" ? 100 : req.package === "pro_500" ? 500 : 1000;
      toast.success(`Approved → +${credits} listings to ${req.seller_email}`);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    } finally {
      setApproving(null);
    }
  }

  async function rejectRenewal(req) {
    if (!window.confirm(`Reject renewal request from ${req.seller_email}?`)) return;
    try {
      await api.post(`/admin/renewal-requests/${req.id}/reject`);
      toast.success("Rejected");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  async function approvePendingSub(lead) {
    if (!window.confirm(`Approve ${lead.email}? This creates the seller account, credits +${lead.listings} listings and emails login credentials.`)) return;
    setApprovingSub(lead.id);
    try {
      const { data } = await api.post(`/admin/pending-subscriptions/${lead.id}/approve`);
      toast.success(
        `Approved → ${lead.email} (+${lead.listings} listings)` +
          (data.password_generated ? " — credentials emailed" : " — account topped up")
      );
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    } finally {
      setApprovingSub(null);
    }
  }

  async function rejectPendingSub(lead) {
    const reason = window.prompt(`Reject subscription lead from ${lead.email}? Reason (optional):`);
    if (reason === null) return;
    try {
      await api.post(`/admin/pending-subscriptions/${lead.id}/reject`, { reason });
      toast.success("Lead rejected");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  async function deletePendingSub(lead) {
    if (!window.confirm(`Delete lead from ${lead.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pending-subscriptions/${lead.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  }

  function openTierModal(s) {
    setTierTarget(s);
    setTierDraft(s.plan_tier || "business");
  }

  async function saveTier() {
    if (!tierTarget) return;
    if (!["starter", "pro", "business"].includes(tierDraft)) {
      toast.error("Pick a tier");
      return;
    }
    setSavingTier(true);
    try {
      const { data } = await api.post(`/admin/sellers/${tierTarget.id}/plan-tier`, { plan_tier: tierDraft });
      toast.success(`Tier set to ${data.plan_tier} — ${tierTarget.email}`);
      setTierTarget(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    } finally {
      setSavingTier(false);
    }
  }

  function daysLeft(exp) {
    if (!exp) return "—";
    const ms = new Date(exp).getTime() - Date.now();
    const d = Math.ceil(ms / (1000 * 60 * 60 * 24));
    return d > 0 ? `${d}d` : "0d";
  }

  return (
    <AppShell role="admin">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">Sellers</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">Seller roster.</h1>
          <p className="text-white/60 text-sm mt-1">Issue credentials, toggle access, renew subscriptions.</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search by email or brand name…"
            className="px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.12] text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#7C3AED] w-72"
            data-testid="seller-search-input"
          />
          <button className="btn-primary text-sm" onClick={() => setShowCreate(true)} data-testid="create-seller-btn">
            <Plus size={14} className="inline mr-1" /> Create Seller
          </button>
        </div>
      </div>

      {/* Pending renewal requests — seller has paid via UPI, waiting for admin verify */}
      {renewals.filter((r) => r.status === "pending").length > 0 && (
        <div
          className="rounded-xl border mb-6 overflow-hidden"
          style={{ borderColor: "#FFC9D5", background: "#FFF5F8" }}
          data-testid="pending-renewals"
        >
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "#FFD4DE" }}>
            <AlertCircle size={14} style={{ color: "#FF3F6C" }} />
            <div className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: "#FF3F6C" }}>
              Pending renewal requests — {renewals.filter((r) => r.status === "pending").length}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider" style={{ color: "#535665" }}>
                <th className="text-left px-4 py-2 font-bold">Seller</th>
                <th className="text-left px-4 py-2 font-bold">Package</th>
                <th className="text-left px-4 py-2 font-bold">Txn ID</th>
                <th className="text-left px-4 py-2 font-bold">Notes</th>
                <th className="text-left px-4 py-2 font-bold">Received</th>
                <th className="text-right px-4 py-2 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {renewals
                .filter((r) => r.status === "pending")
                .map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: "#FFE3EA", color: "#282C3F" }}>
                    <td className="px-4 py-2.5">
                      <div className="font-bold">{r.seller_name}</div>
                      <div className="text-[11px]" style={{ color: "#535665" }}>{r.seller_email}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-bold">{r.package_label}</div>
                      <div className="text-[11px]" style={{ color: "#535665" }}>₹{r.price_inr}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.transaction_id || "—"}</td>
                    <td className="px-4 py-2.5 text-[12px]" style={{ color: "#535665" }}>{r.notes || "—"}</td>
                    <td className="px-4 py-2.5 text-[11px]" style={{ color: "#535665" }}>
                      {new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => approveRenewal(r)}
                          disabled={approving === r.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold disabled:opacity-50"
                          style={{ background: "#03A685", color: "#FFFFFF" }}
                          data-testid={`approve-renewal-${r.id}`}
                        >
                          {approving === r.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                          Approve & add credits
                        </button>
                        <button
                          onClick={() => rejectRenewal(r)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold"
                          style={{ background: "#FFFFFF", color: "#FF3F6C", border: "1px solid #FFC9D5" }}
                          data-testid={`reject-renewal-${r.id}`}
                        >
                          <X size={12} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending paid-checkout subscription leads (Pricing page form → QR flow) */}
      {pendingSubs.filter((l) => l.status !== "approved" && l.status !== "rejected").length > 0 && (
        <div
          className="rounded-xl border mb-6 overflow-hidden"
          style={{ borderColor: "#C7D2FE", background: "#EEF2FF" }}
          data-testid="pending-subscriptions"
        >
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "#C7D2FE" }}>
            <AlertCircle size={14} style={{ color: "#4F46E5" }} />
            <div className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: "#4F46E5" }}>
              Pending subscription checkouts — {pendingSubs.filter((l) => l.status !== "approved" && l.status !== "rejected").length}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider" style={{ color: "#475569" }}>
                <th className="text-left px-4 py-2 font-bold">Brand / Company</th>
                <th className="text-left px-4 py-2 font-bold">Contact</th>
                <th className="text-left px-4 py-2 font-bold">Plan</th>
                <th className="text-left px-4 py-2 font-bold">Status</th>
                <th className="text-left px-4 py-2 font-bold">Txn ID</th>
                <th className="text-left px-4 py-2 font-bold">Received</th>
                <th className="text-right px-4 py-2 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {pendingSubs
                .filter((l) => l.status !== "approved" && l.status !== "rejected")
                .map((l) => {
                  const waDigits = (l.phone || "").replace(/[^0-9]/g, "").slice(-10);
                  const waUrl = waDigits ? `https://wa.me/91${waDigits}` : null;
                  return (
                    <tr key={l.id} className="border-t" style={{ borderColor: "#DDD6FE", color: "#1E1B4B" }}>
                      <td className="px-4 py-2.5">
                        <div className="font-bold">{l.brand_name}</div>
                        <div className="text-[11px]" style={{ color: "#475569" }}>{l.company}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-[12px]">
                          <a href={`mailto:${l.email}`} className="font-mono text-[11px]" style={{ color: "#4F46E5" }}>
                            {l.email}
                          </a>
                        </div>
                        <div className="text-[11px]" style={{ color: "#475569" }}>
                          {waUrl ? (
                            <a href={waUrl} target="_blank" rel="noreferrer" className="font-mono" style={{ color: "#03A685" }}>
                              {l.phone}
                            </a>
                          ) : (
                            l.phone || "—"
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="font-bold">{l.package_label}</div>
                        <div className="text-[11px]" style={{ color: "#475569" }}>
                          ₹{Number(l.price_inr).toLocaleString("en-IN")} · {l.listings} listings
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                          style={
                            l.status === "awaiting_verification"
                              ? { background: "#FEF3C7", color: "#92400E" }
                              : { background: "#E0E7FF", color: "#4338CA" }
                          }
                        >
                          {l.status === "awaiting_verification" ? "Paid – verify" : "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{l.transaction_id || "—"}</td>
                      <td className="px-4 py-2.5 text-[11px]" style={{ color: "#475569" }}>
                        {new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => approvePendingSub(l)}
                            disabled={approvingSub === l.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold disabled:opacity-50"
                            style={{ background: "#03A685", color: "#FFFFFF" }}
                            data-testid={`approve-pending-sub-${l.id}`}
                          >
                            {approvingSub === l.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                            Approve & create
                          </button>
                          <button
                            onClick={() => rejectPendingSub(l)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold"
                            style={{ background: "#FFFFFF", color: "#DC2626", border: "1px solid #FCA5A5" }}
                            data-testid={`reject-pending-sub-${l.id}`}
                          >
                            <X size={12} /> Reject
                          </button>
                          <button
                            onClick={() => deletePendingSub(l)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold"
                            style={{ background: "#FFFFFF", color: "#475569", border: "1px solid #CBD5E1" }}
                            data-testid={`delete-pending-sub-${l.id}`}
                            title="Delete lead"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl text-white overflow-hidden" data-testid="sellers-table">
        {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
        {!loading && sellers.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No sellers yet. Create your first.</div>}
        {!loading && sellers.length > 0 && (
          <table className="w-full">
            <thead>
              <tr className="bg-[#4F46E5] text-white text-[10px] uppercase tracking-[0.2em]">
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                <th className="text-left px-4 py-3 font-bold">Company</th>
                <th className="text-left px-4 py-3 font-bold">Platforms</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Expires</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sellers
                .filter((s) => {
                  const q = searchQ.trim().toLowerCase();
                  if (!q) return true;
                  return [s.name, s.email, s.company]
                    .some((v) => (v || "").toLowerCase().includes(q));
                })
                .map((s, i) => (
                <tr key={s.id} className={`${i % 2 === 0 ? "bg-white/[0.04]" : "bg-white/[0.02]"}`}>
                  <td className="px-4 py-3 text-sm font-bold">{s.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-300 font-mono-data">{s.email}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{s.company || "—"}</td>
                  <td className="px-4 py-3" data-testid={`platforms-cell-${s.id}`}>
                    <div className="flex flex-wrap gap-1">
                      {PLATFORMS.map((meta) => {
                        const list = s.allowed_platforms || [];
                        const on = list.length === 0 || list.includes(meta.id);
                        return (
                          <span
                            key={meta.id}
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                            style={{
                              background: on ? `${meta.color}22` : "rgba(148,150,159,0.12)",
                              color: on ? meta.color : "#94969F",
                              border: `1px solid ${on ? `${meta.color}55` : "rgba(148,150,159,0.3)"}`,
                              opacity: on ? 1 : 0.6,
                            }}
                            title={on ? `${meta.id} enabled` : `${meta.id} disabled`}
                          >
                            {meta.id}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${
                      s.subscription_status === "active" ? "bg-green-100 text-green-700" :
                      s.subscription_status === "expired" ? "bg-orange-100 text-orange-700" :
                      "bg-white/[0.05] text-slate-300"
                    }`}>
                      {s.subscription_status}
                    </span>
                    {s.account_type === "trial" && (
                      <span
                        className="ml-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-indigo-100 text-indigo-700"
                        title="Free trial account — Renew to upgrade to full"
                        data-testid={`trial-badge-${s.id}`}
                      >
                        trial {s.trial_listings_used || 0}/{s.trial_listings_limit || 1}
                      </span>
                    )}
                    {s.account_type !== "trial" && (s.listings_credits > 0 || s.listings_credits_used > 0) && (
                      <span
                        className="ml-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700"
                        title="Listing credits"
                        data-testid={`credits-badge-${s.id}`}
                      >
                        {(s.listings_credits || 0) - (s.listings_credits_used || 0)} / {s.listings_credits || 0} left
                      </span>
                    )}
                    {s.account_locked && (
                      <span
                        className="ml-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-rose-100 text-rose-700"
                        title={`Locked: quota exhausted${s.account_locked_at ? ` on ${new Date(s.account_locked_at).toLocaleDateString()}` : ""}`}
                        data-testid={`locked-badge-${s.id}`}
                      >
                        🔒 Locked
                      </span>
                    )}
                    {(s.paid_features || []).length > 0 && (
                      <span
                        className="ml-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-pink-100 text-pink-700"
                        title={`Paid features: ${(s.paid_features || []).join(", ")}`}
                      >
                        +{s.paid_features.length} paid
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono-data text-slate-400">{daysLeft(s.subscription_expires_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1 flex-wrap justify-end">
                      <button onClick={() => openPlatformEditor(s)} className="p-1.5 rounded hover:bg-slate-100 text-[#535665]" title="Edit platform access" data-testid={`platforms-${s.id}`}>
                        <Layers size={14} />
                      </button>
                      <button onClick={() => openPermEditor(s)} className="p-1.5 rounded hover:bg-slate-100 text-[#535665]" title="Paid feature permissions" data-testid={`perms-${s.id}`}>
                        <Settings2 size={14} />
                      </button>
                      <button onClick={() => openTierModal(s)} className="p-1.5 rounded hover:bg-slate-100 text-[#535665]" title={`Plan tier: ${s.plan_tier || "business"}`} data-testid={`tier-${s.id}`}>
                        <Crown size={14} />
                      </button>
                      <button onClick={() => setCreditTarget(s)} className="p-1.5 rounded hover:bg-slate-100 text-[#535665]" title="Grant listing credits" data-testid={`credits-${s.id}`}>
                        <Wallet size={14} />
                      </button>
                      <button onClick={() => onRenew(s.id)} className="p-1.5 rounded hover:bg-slate-100 text-[#535665]" title="Renew 30 days" data-testid={`renew-${s.id}`}>
                        <RefreshCw size={14} />
                      </button>
                      <button onClick={() => setResetTarget(s)} className="p-1.5 rounded hover:bg-slate-100 text-[#535665]" title="Reset password" data-testid={`reset-${s.id}`}>
                        <KeyRound size={14} />
                      </button>
                      <button onClick={() => onToggle(s.id, s.subscription_status)} className="p-1.5 rounded hover:bg-slate-100 text-[#535665]" title="Toggle access" data-testid={`toggle-${s.id}`}>
                        {s.subscription_status === "disabled" ? <CheckCircle2 size={14} /> : <Ban size={14} />}
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded font-bold text-xs"
                        style={{ background: "#FFE3EA", color: "#FF3F6C", border: "1px solid #FF3F6C44" }}
                        title="Permanently delete seller"
                        data-testid={`delete-${s.id}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Seller" testId="create-seller-modal">
        <form onSubmit={onCreate} className="space-y-3">
          {[
            ["name", "Full name"],
            ["email", "Email"],
            ["password", "Initial password (min 8 chars)"],
            ["company", "Company (optional)"],
            ["phone", "Phone (optional)"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</label>
              <input
                type={k === "password" ? "password" : k === "email" ? "email" : "text"}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                required={["name", "email", "password"].includes(k)}
                minLength={k === "password" ? 8 : undefined}
                className="mt-1 w-full px-3 py-2 border border-white/[0.08] bg-white/[0.04] text-white placeholder:text-slate-500 rounded-md focus:outline-none focus:border-[#4F46E5]"
                data-testid={`create-seller-${k}`}
              />
            </div>
          ))}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Subscription days</label>
            <input
              type="number"
              min={1}
              value={form.subscription_days}
              onChange={(e) => setForm({ ...form, subscription_days: Number(e.target.value) })}
              className="mt-1 w-full px-3 py-2 border border-white/[0.08] bg-white/[0.04] text-white placeholder:text-slate-500 rounded-md focus:outline-none focus:border-[#4F46E5]"
              data-testid="create-seller-days"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Platform access</label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="create-seller-platforms">
              {PLATFORMS.map((p) => {
                const on = form.allowed_platforms.includes(p.id);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => togglePlatformInDraft(p.id, "create")}
                    data-testid={`create-platform-${p.id.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border-2 transition-all ${
                      on
                        ? "border-[#4F46E5] bg-[#4F46E5] text-white"
                        : "border-white/[0.08] text-slate-500 hover:border-zinc-400"
                    }`}
                  >
                    {on ? "✓ " : ""}{p.id}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5">
              Seller can only upload data for the selected platforms.
            </div>
          </div>
          <button type="submit" className="btn-primary w-full mt-2" data-testid="create-seller-submit">Create Seller</button>
        </form>
      </Modal>

      <Modal
        open={!!platformTarget}
        onClose={() => setPlatformTarget(null)}
        title={`Platforms for ${platformTarget?.name || ""}`}
        testId="platforms-modal"
      >
        <form onSubmit={onSavePlatforms} className="space-y-3">
          <div className="text-xs text-slate-400">
            Pick which platforms <span className="font-bold">{platformTarget?.name}</span> is allowed to upload.
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            {PLATFORMS.map((p) => {
              const on = platformDraft.includes(p.id);
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => togglePlatformInDraft(p.id, "edit")}
                  data-testid={`edit-platform-${p.id.toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border-2 transition-all ${
                    on
                      ? "border-[#4F46E5] bg-[#4F46E5] text-white"
                      : "border-white/[0.08] text-slate-500 hover:border-zinc-400"
                  }`}
                >
                  {on ? "✓ " : ""}{p.id}
                </button>
              );
            })}
          </div>
          <button type="submit" className="btn-primary w-full mt-2" data-testid="save-platforms-submit">
            Save platforms
          </button>
        </form>
      </Modal>

      <Modal open={!!resetTarget} onClose={() => setResetTarget(null)} title={`Reset password for ${resetTarget?.name || ""}`} testId="reset-password-modal">
        <form onSubmit={onResetPassword} className="space-y-3">
          <input
            type="password"
            required
            minLength={8}
            placeholder="New password (min 8 chars)"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            className="w-full px-3 py-2 border border-white/[0.08] rounded-md focus:outline-none focus:border-[#4F46E5]"
            data-testid="reset-password-input"
          />
          <button type="submit" className="btn-primary w-full" data-testid="reset-password-submit">Reset Password</button>
        </form>
      </Modal>

      {/* Paid feature permissions */}
      <Modal
        open={!!permTarget}
        onClose={() => setPermTarget(null)}
        title={`Permissions — ${permTarget?.email || ""}`}
        testId="permissions-modal"
      >
        <div className="space-y-3 text-white">
          <p className="text-xs text-slate-400">
            Toggle paid features for this seller. Grants take effect immediately.
          </p>
          {[
            ["competitor_scraping", "Competitor Sniper", "Live Myntra/Nykaa/Ajio competitor pricing"],
          ].map(([key, label, hint]) => {
            const on = permDraft.includes(key);
            return (
              <div
                key={key}
                className="flex items-center justify-between px-3 py-2.5 rounded-md border border-white/[0.08]"
                data-testid={`perm-row-${key}`}
              >
                <div>
                  <div className="text-sm font-bold">{label}</div>
                  <div className="text-[11px] text-slate-400">{hint}</div>
                </div>
                <button
                  onClick={() => togglePerm(key)}
                  className="px-3 py-1.5 rounded-md text-xs font-bold"
                  style={{
                    background: on ? "#03A685" : "transparent",
                    color: on ? "#FFFFFF" : "#FF3F6C",
                    border: on ? "1px solid #03A685" : "1px solid #FF3F6C66",
                  }}
                  data-testid={`perm-toggle-${key}`}
                >
                  {on ? "Enabled" : "Enable"}
                </button>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* Credit grant */}
      <Modal
        open={!!creditTarget}
        onClose={() => setCreditTarget(null)}
        title={`Grant listings to ${creditTarget?.email || ""}`}
        testId="credits-modal"
      >
        <div className="space-y-3 text-white">
          <p className="text-xs text-slate-400">
            Add listing credits — use this after the seller pays via UPI/cash.
            Current balance: <span className="font-bold text-white">
              {(creditTarget?.listings_credits || 0) - (creditTarget?.listings_credits_used || 0)}
            </span> remaining.
          </p>
          <button
            onClick={() => grantPackage(creditTarget, "starter_100")}
            className="w-full flex items-center justify-between px-4 py-3 rounded-md border border-white/[0.08] hover:border-[#FF3F6C]"
            data-testid="grant-starter"
          >
            <div className="text-left">
              <div className="text-sm font-bold">Starter — +100 listings</div>
              <div className="text-[11px] text-slate-400">₹1,999 plan</div>
            </div>
            <div className="text-[#FF3F6C] font-extrabold">+100</div>
          </button>
          <button
            onClick={() => grantPackage(creditTarget, "pro_500")}
            className="w-full flex items-center justify-between px-4 py-3 rounded-md border border-white/[0.08] hover:border-[#FF3F6C]"
            data-testid="grant-pro"
          >
            <div className="text-left">
              <div className="text-sm font-bold">Pro — +500 listings</div>
              <div className="text-[11px] text-slate-400">₹4,999 plan</div>
            </div>
            <div className="text-[#FF3F6C] font-extrabold">+500</div>
          </button>
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="Custom (any number)"
              className="flex-1 px-3 py-2 border border-white/[0.08] rounded-md focus:outline-none focus:border-[#4F46E5]"
              data-testid="grant-custom-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") grantCustom(creditTarget, e.currentTarget.value);
              }}
            />
            <button
              onClick={(e) => {
                const inp = e.currentTarget.previousElementSibling;
                grantCustom(creditTarget, inp.value);
              }}
              className="px-3 py-2 rounded-md text-sm font-bold"
              style={{ background: "#FF3F6C", color: "#FFFFFF" }}
              data-testid="grant-custom-btn"
            >
              Add
            </button>
          </div>

          {/* ═════════ NEW: Set EXACT quota (auto-lock when exhausted) ═════════ */}
          <div className="mt-4 pt-4 border-t border-white/[0.08]">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-black text-amber-300">
                  Set exact quota
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Overwrites balance · resets usage · auto-locks when consumed
                </div>
              </div>
              {creditTarget?.account_locked && (
                <span className="text-[9px] uppercase tracking-widest font-bold text-rose-300 border border-rose-400/30 bg-rose-500/10 px-1.5 py-0.5 rounded" data-testid="seller-locked-badge">
                  Locked
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {[100, 200, 300, 500, 1000, 2000].map((n) => (
                <button
                  key={n}
                  onClick={() => setExactQuota(creditTarget, n, true)}
                  className="px-2 py-2 rounded-md border border-amber-400/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-100 text-sm font-bold transition-colors"
                  data-testid={`set-quota-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Any number (e.g. 250)"
                className="flex-1 px-3 py-2 border border-amber-400/30 bg-amber-500/[0.06] rounded-md text-amber-50 focus:outline-none focus:border-amber-400"
                data-testid="set-quota-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter") setExactQuota(creditTarget, e.currentTarget.value, true);
                }}
              />
              <button
                onClick={(e) => {
                  const inp = e.currentTarget.previousElementSibling;
                  setExactQuota(creditTarget, inp.value, true);
                }}
                className="px-3 py-2 rounded-md text-sm font-bold bg-amber-500 hover:bg-amber-400 text-black"
                data-testid="set-quota-btn"
              >
                Set quota
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
              When the seller uses all N listings, their account will
              auto-lock. To re-open, set a new quota here.
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete seller confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => { setDeleteTarget(null); setDeleteConfirm(""); }}
        title="Delete seller — permanent"
        testId="delete-seller-modal"
      >
        {deleteTarget && (
          <div className="space-y-4 text-sm">
            <div
              className="rounded-lg p-3 border"
              style={{ borderColor: "#FCA5A5", background: "rgba(220,38,38,0.10)", color: "#FECACA" }}
            >
              <div className="font-bold mb-1">This cannot be undone.</div>
              <div className="text-[12px]">
                The seller account <b className="font-mono">{deleteTarget.email}</b>, all their
                uploads and generated reports will be permanently deleted.
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                Type <span className="font-mono text-rose-400">delete</span> to confirm
              </label>
              <input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="delete"
                className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.1] rounded-md text-white placeholder:text-slate-600 focus:outline-none focus:border-rose-400"
                data-testid="delete-confirm-input"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }}
                className="px-3 py-2 rounded-md text-sm font-bold border border-white/[0.15] text-slate-200 hover:bg-white/[0.04]"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting || deleteConfirm.trim().toLowerCase() !== "delete"}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-bold disabled:opacity-50"
                style={{ background: "#DC2626", color: "#FFFFFF" }}
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete permanently
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Plan tier picker — Starter / Pro / Business */}
      <Modal
        open={!!tierTarget}
        onClose={() => setTierTarget(null)}
        title="Set Plan Tier"
        testId="tier-modal"
      >
        {tierTarget && (
          <div className="space-y-4 text-sm">
            <div className="text-slate-400 text-[12px]">
              Pick the access level for <b className="font-mono text-white">{tierTarget.email}</b>.
              Tier controls which sidebar tabs the seller can use.
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { id: "starter",  label: "Listings Only",             pitch: "Only AI Auto-Listings + Profile. No data-analysis tabs.", credits: "Listings access" },
                { id: "pro",      label: "Listings + Data Analysis",  pitch: "Listings plus Dashboard, Growth Analytics, Ads Suggestions, Reports.", credits: "Analysis access" },
                { id: "business", label: "Full Access",               pitch: "Everything unlocked — incl. AI Photoshoot, BG Remover, My Catalogue.", credits: "All features" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setTierDraft(opt.id)}
                  className="text-left rounded-lg p-3 border transition"
                  style={{
                    borderColor: tierDraft === opt.id ? "#7C3AED" : "rgba(255,255,255,0.08)",
                    background:   tierDraft === opt.id ? "rgba(124,58,237,0.18)" : "rgba(255,255,255,0.02)",
                  }}
                  data-testid={`tier-opt-${opt.id}`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className="font-bold text-white">{opt.label}</div>
                    <div className="text-[11px] text-indigo-300">{opt.credits}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{opt.pitch}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setTierTarget(null)}
                className="px-3 py-2 rounded-md text-sm font-bold border border-white/[0.15] text-slate-200 hover:bg-white/[0.04]"
                data-testid="tier-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={saveTier}
                disabled={savingTier}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-bold disabled:opacity-50"
                style={{ background: "#7C3AED", color: "#FFFFFF" }}
                data-testid="tier-save-btn"
              >
                {savingTier ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />}
                Save tier
              </button>
            </div>
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
